import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Switch,
  Typography, Checkbox, FormGroup,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import {
  AgendaConfig, AgendaJob, agendaActionsForType, formatJobTime,
  getDeviceSchedules, normalizeAgendaConfig, updateDeviceSchedules,
} from '../services/deviceSchedulesService';
import DeferredNumberField from './shared/DeferredNumberField';
import { eventBus } from '../services/EventBus';

const ACCENT = '#2391FF';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(255,255,255,0.45)';
const MAX_JOBS = 20;
const DAY_KEYS = [1, 2, 3, 4, 5, 6, 7]; // Lun..Dom
const EDIT_STEPS = 3;

/** Menú desplegable alineado con filtros de DeviceModal / habitación. */
const MENU_PAPER_SX = {
  backgroundColor: 'rgba(18,18,20,0.98)',
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '12px',
  color: '#fff',
  '& .MuiMenuItem-root': {
    fontSize: 12,
    minHeight: 32,
    '&:hover': { backgroundColor: 'rgba(35,145,255,0.14)' },
    '&.Mui-selected': { backgroundColor: 'rgba(35,145,255,0.22)' },
  },
};

const TIME_FIELD_SX = {
  flex: 1,
  minWidth: 0,
  input: { color: '#fff' },
  '& .MuiInputLabel-root': { color: MUTED, fontSize: '0.75rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.7rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
  '&.Mui-disabled': { opacity: 0.45 },
  '& .MuiInputBase-root.Mui-disabled': { opacity: 0.7 },
};

const switchAccentSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
  '&.Mui-disabled': { opacity: 0.4 },
};

const chipBtnSx = (active: boolean, disabled?: boolean) => ({
  minWidth: '20px',
  height: '20px',
  fontSize: '0.6rem',
  px: 0.5,
  backgroundColor: active ? ACCENT : 'transparent',
  color: active ? 'white' : '#aaa',
  borderColor: SURFACE_BORDER,
  opacity: disabled ? 0.4 : 1,
  '&:hover': {
    backgroundColor: active ? '#1a7ae6' : '#333',
  },
});

function formatToggleAfter(seconds: number): string {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m} min`);
  if (sec || !parts.length) parts.push(`${sec}s`);
  return parts.join(' ');
}

type AgendaCaps = {
  gen1: boolean;
  supportsSeconds: boolean;
  supportsDomMonths: boolean;
  supportsToggleAfter: boolean;
  actions: string[];
};

function agendaCapsForDevice(tipo?: string, generacion?: number): AgendaCaps {
  const t = String(tipo || '').toUpperCase();
  // Misma regla que el panel previo: generación 1 o DIMMER2 (legacy sin gen en BD).
  const gen1 = Number(generacion) === 1 || t === 'DIMMER2';
  const isCover = t === 'PLUS2PM';
  const isPulse = t === 'PLUS1';
  // Gen1 Dimmer "Set duration" = ramp/transición, no toggle_after → no exponer en UI.
  return {
    gen1,
    supportsSeconds: !gen1,
    supportsDomMonths: !gen1,
    supportsToggleAfter: !gen1 && !isCover && !isPulse,
    actions: agendaActionsForType(tipo, generacion),
  };
}

function clampJobForCaps(job: AgendaJob, caps: AgendaCaps): AgendaJob {
  const next: AgendaJob = { ...job };
  if (!caps.supportsSeconds) next.seconds = 0;
  if (!caps.supportsDomMonths) {
    next.specificDaysEnabled = false;
    next.selectedDaysOfMonth = [];
    next.specificMonthsEnabled = false;
    next.selectedMonths = [];
  }
  if (!caps.supportsToggleAfter || !['On', 'Off'].includes(String(next.actionType))) {
    next.toggleEnabled = false;
    next.toggleAfterSeconds = 0;
  }
  if (!caps.actions.includes(String(next.actionType))) {
    next.actionType = caps.actions[0] || 'On';
  }
  if (!next.selectedDays?.length) next.selectedDays = [1, 2, 3, 4, 5, 6, 7];
  return next;
}

type Props = {
  device: { id: number; tipo?: string; generacion?: number; nombre?: string; agenda_config?: AgendaConfig };
  canControl: boolean;
  onDemoBlock?: () => void;
  onPermissionBlock?: () => void;
  isDemo?: boolean;
  cardSx?: object;
};

function actionLabel(t: (k: string) => string, action: string): string {
  const map: Record<string, string> = {
    On: 'deviceModal.agendaActionOn',
    Off: 'deviceModal.agendaActionOff',
    Toggle: 'deviceModal.agendaActionToggle',
    Pulse: 'deviceModal.agendaActionPulse',
    Open: 'deviceModal.agendaActionOpen',
    Close: 'deviceModal.agendaActionClose',
    Stop: 'deviceModal.agendaActionStop',
    GoToPosition: 'deviceModal.agendaActionGoto',
  };
  const key = map[action];
  return key ? (t(key) as string) : action;
}

/** Etiqueta corta para pastillas del resumen en DeviceModal. */
function actionShort(t: (k: string) => string, action: string): string {
  const map: Record<string, string> = {
    On: 'deviceModal.agendaActionOnShort',
    Off: 'deviceModal.agendaActionOffShort',
    Toggle: 'deviceModal.agendaActionToggleShort',
    Pulse: 'deviceModal.agendaActionPulseShort',
    Open: 'deviceModal.agendaActionOpenShort',
    Close: 'deviceModal.agendaActionCloseShort',
    Stop: 'deviceModal.agendaActionStopShort',
    GoToPosition: 'deviceModal.agendaActionGotoShort',
  };
  const key = map[action];
  return key ? (t(key) as string) : actionLabel(t, action);
}

function actionChipColors(action: string): { bg: string; border: string; text: string } {
  switch (action) {
    case 'On':
      return { bg: 'rgba(46, 204, 113, 0.18)', border: 'rgba(46, 204, 113, 0.55)', text: '#7dffa8' };
    case 'Off':
      return { bg: 'rgba(255, 152, 0, 0.18)', border: 'rgba(255, 152, 0, 0.55)', text: '#ffcc80' };
    case 'Toggle':
      return { bg: 'rgba(35, 145, 255, 0.18)', border: 'rgba(35, 145, 255, 0.55)', text: '#90caf9' };
    case 'Pulse':
      return { bg: 'rgba(171, 71, 188, 0.18)', border: 'rgba(171, 71, 188, 0.55)', text: '#ce93d8' };
    case 'Open':
    case 'Close':
    case 'Stop':
    case 'GoToPosition':
      return { bg: 'rgba(38, 198, 218, 0.18)', border: 'rgba(38, 198, 218, 0.55)', text: '#80deea' };
    default:
      return { bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)', text: '#ddd' };
  }
}

function daysShort(t: (k: string, o?: any) => string, days: number[]): string {
  if (days.length >= 7) return t('deviceModal.agendaEveryDay') as string;
  const labels = ['', 'L', 'M', 'X', 'J', 'V', 'S', 'D'];
  return days.map((d) => labels[d] || d).join('');
}

const MONTH_KEYS = [
  'deviceModal.months.jan', 'deviceModal.months.feb', 'deviceModal.months.mar',
  'deviceModal.months.apr', 'deviceModal.months.may', 'deviceModal.months.jun',
  'deviceModal.months.jul', 'deviceModal.months.aug', 'deviceModal.months.sep',
  'deviceModal.months.oct', 'deviceModal.months.nov', 'deviceModal.months.dec',
];

function jobRecurrenceExtra(
  t: (k: string, o?: any) => string,
  j: AgendaJob,
): string {
  const parts: string[] = [];
  if (j.specificDaysEnabled && j.selectedDaysOfMonth?.length) {
    parts.push(`${t('deviceModal.days')} ${j.selectedDaysOfMonth.join(',')}`);
  }
  if (j.specificMonthsEnabled && j.selectedMonths?.length) {
    parts.push(
      j.selectedMonths
        .map((m) => t(MONTH_KEYS[m - 1] || '') as string)
        .filter(Boolean)
        .join(','),
    );
  }
  return parts.length ? ` · ${parts.join(' · ')}` : '';
}

/** Línea compacta de recurrencia para pastillas del resumen. */
function chipDaysLine(t: (k: string, o?: any) => string, j: AgendaJob): string {
  const parts: string[] = [daysShort(t, j.selectedDays || [])];
  if (j.specificDaysEnabled && j.selectedDaysOfMonth?.length) {
    const days = [...j.selectedDaysOfMonth].sort((a, b) => a - b);
    parts.push(days.length <= 4 ? `d${days.join(',')}` : `d×${days.length}`);
  }
  if (j.specificMonthsEnabled && j.selectedMonths?.length) {
    const months = [...j.selectedMonths].sort((a, b) => a - b);
    if (months.length <= 3) {
      parts.push(months.map((m) => t(MONTH_KEYS[m - 1] || '') as string).filter(Boolean).join(','));
    } else {
      parts.push(`m×${months.length}`);
    }
  }
  return parts.join(' · ');
}

const blankJob = (id: number, tipo?: string, gen?: number): AgendaJob => {
  const caps = agendaCapsForDevice(tipo, gen);
  return clampJobForCaps({
    id,
    enabled: true,
    hours: 8,
    minutes: 0,
    seconds: 0,
    selectedDays: [1, 2, 3, 4, 5, 6, 7],
    specificDaysEnabled: false,
    selectedDaysOfMonth: [],
    specificMonthsEnabled: false,
    selectedMonths: [],
    actionType: caps.actions[0] || 'On',
    toggleEnabled: false,
    toggleAfterSeconds: 0,
    brightness: null,
    coverPosition: 50,
    editable: true,
  }, caps);
};

const DeviceAgendaPanel: React.FC<Props> = ({
  device, canControl, onDemoBlock, onPermissionBlock, isDemo, cardSx,
}) => {
  const { t } = useTranslation();
  const caps = useMemo(
    () => agendaCapsForDevice(device.tipo, device.generacion),
    [device.tipo, device.generacion],
  );
  const [agenda, setAgenda] = useState<AgendaConfig>(() =>
    normalizeAgendaConfig(device.agenda_config),
  );
  const [shellyHad, setShellyHad] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [editJob, setEditJob] = useState<AgendaJob | null>(null);
  const [editStep, setEditStep] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<AgendaConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const loadSeq = useRef(0);
  const pendingRemoteRef = useRef<AgendaConfig | null>(null);
  const editingRef = useRef(false);
  editingRef.current = Boolean(listOpen || editJob || saving || confirmOpen);

  const load = useCallback(async () => {
    // Cache en el servicio: si ya hidratamos desde el mapa, el GET es rápido
    // (el backend ya no sondea Shelly cuando hay agenda en BD).
    const seq = ++loadSeq.current;
    const data = await getDeviceSchedules(device.id, false);
    if (seq !== loadSeq.current) return;
    setAgenda(normalizeAgendaConfig(data.agenda_config));
    setShellyHad(Boolean(data.shelly_has_schedule));
  }, [device.id]);

  useEffect(() => {
    // Hidratar al instante desde el mapa (evita flash vacío + espera HTTP).
    // Solo al cambiar de dispositivo: agenda_config del prop se lee aquí a propósito.
    setAgenda(normalizeAgendaConfig(device.agenda_config));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync on device.id via load
  }, [device.id, load]);

  useEffect(() => {
    const handleSchedulesUpdate = (data: { id?: number; agenda_config?: AgendaConfig }) => {
      if (!data || Number(data.id) !== Number(device.id) || !data.agenda_config) return;
      loadSeq.current += 1;
      const next = normalizeAgendaConfig(data.agenda_config);
      if (editingRef.current) {
        pendingRemoteRef.current = next;
        return;
      }
      pendingRemoteRef.current = null;
      setAgenda(next);
    };
    eventBus.on('device:schedules_updated', handleSchedulesUpdate);
    return () => {
      eventBus.off('device:schedules_updated', handleSchedulesUpdate);
    };
  }, [device.id]);

  useEffect(() => {
    if (editingRef.current || !pendingRemoteRef.current) return;
    setAgenda(pendingRemoteRef.current);
    pendingRemoteRef.current = null;
  }, [listOpen, editJob, saving, confirmOpen]);

  const activeCount = useMemo(
    () => agenda.jobs.filter((j) => j.enabled).length,
    [agenda.jobs],
  );

  const guard = (e?: React.SyntheticEvent) => {
    if (isDemo) {
      e?.stopPropagation();
      onDemoBlock?.();
      return false;
    }
    if (!canControl) {
      e?.stopPropagation();
      onPermissionBlock?.();
      return false;
    }
    return true;
  };

  const persist = async (cfg: AgendaConfig, opts?: { confirmClear?: boolean }) => {
    setSaving(true);
    setError('');
    const normalized = normalizeAgendaConfig(cfg);
    const confirmClear = Boolean(opts?.confirmClear) || (normalized.jobs.length === 0 && !shellyHad);
    const res = await updateDeviceSchedules(device.id, undefined, normalized, {
      confirmClearSchedule: confirmClear || normalized.jobs.length > 0,
    });
    setSaving(false);
    if (!res.success) {
      setError(res.message);
      return false;
    }
    setAgenda(normalizeAgendaConfig(res.agenda_config || normalized));
    setShellyHad(Boolean(res.shelly_has_schedule) || shellyHad);
    return true;
  };

  const requestSave = async (cfg: AgendaConfig) => {
    if (shellyHad) {
      setPendingSave(cfg);
      setConfirmOpen(true);
      return;
    }
    await persist(cfg);
  };

  const onMaster = async (enabled: boolean) => {
    if (!guard()) return;
    const next = { ...agenda, enabled };
    setAgenda(next);
    await requestSave(next);
  };

  const openList = (e: React.MouseEvent) => {
    if (!guard(e)) return;
    setListOpen(true);
  };

  const saveFromList = async (cfg: AgendaConfig) => {
    setEditJob(null);
    setEditStep(1);
    const ok = await (async () => {
      if (shellyHad) {
        setPendingSave(cfg);
        setConfirmOpen(true);
        return false;
      }
      return persist(cfg);
    })();
    if (ok) setListOpen(false);
  };

  const openEditor = (job: AgendaJob) => {
    setEditJob(clampJobForCaps({ ...job }, caps));
    setEditStep(1);
  };

  const closeEditor = () => {
    setEditJob(null);
    setEditStep(1);
  };

  const commitEditor = () => {
    if (!editJob) return;
    const job = clampJobForCaps(editJob, caps);
    const jobs = agenda.jobs.some((j) => j.id === job.id)
      ? agenda.jobs.map((j) => (j.id === job.id ? job : j))
      : [...agenda.jobs, job];
    setAgenda({ ...agenda, jobs });
    closeEditor();
  };

  const toggleAfterUsable = Boolean(
    editJob
    && caps.supportsToggleAfter
    && ['On', 'Off'].includes(String(editJob.actionType)),
  );

  const stepLabels = [
    t('deviceModal.agendaStepTime') as string,
    t('deviceModal.agendaStepWhen') as string,
    t('deviceModal.agendaStepAction') as string,
  ];

  const disabledHint = (
    <Typography sx={{ color: MUTED, fontSize: '0.65rem', ml: 5, mb: 0.5 }}>
      {t('deviceModal.agendaOptionUnavailable') as string}
    </Typography>
  );

  const summaryJobs = useMemo(() => {
    return [...agenda.jobs].sort((a, b) => {
      const ta = a.hours * 3600 + a.minutes * 60 + (a.seconds || 0);
      const tb = b.hours * 3600 + b.minutes * 60 + (b.seconds || 0);
      return ta - tb;
    });
  }, [agenda.jobs]);

  return (
    <>
      <Card
        sx={{
          p: 1.5,
          pt: 0.65,
          pb: 0.45,
          height: '70px', // ajustado al contenido (sin hueco bajo chips)
          cursor: 'pointer',
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          display: 'flex',
          flexDirection: 'column',
          ...cardSx,
        }}
        onClick={openList}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.75 }}>
            <CalendarMonthIcon sx={{ color: ACCENT, fontSize: '1.05rem', opacity: 0.9 }} />
            <Typography variant="subtitle1" sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>
              {t('deviceModal.agenda')}
            </Typography>
            {agenda.jobs.length > 0 && (
              <Box
                sx={{
                  px: 0.7,
                  py: 0.1,
                  borderRadius: '8px',
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  lineHeight: 1.4,
                  color: agenda.enabled ? '#fff' : '#aaa',
                  backgroundColor: agenda.enabled ? 'rgba(35,145,255,0.35)' : 'rgba(255,255,255,0.08)',
                  border: `1px solid ${agenda.enabled ? 'rgba(35,145,255,0.55)' : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {agenda.enabled
                  ? `${activeCount}/${agenda.jobs.length}`
                  : t('deviceModal.agendaBadgeOff', { total: agenda.jobs.length })}
              </Box>
            )}
          </Box>
          <Switch
            checked={agenda.enabled}
            disabled={!canControl || !agenda.jobs.length}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onMaster(e.target.checked)}
          />
        </Box>

        {!agenda.jobs.length ? (
          <Typography sx={{ color: '#888', fontSize: '0.65rem', mt: 0.38, pl: 3.4 }}>
            {t('deviceModal.agendaEmptyHint') as string}
          </Typography>
        ) : (
          <Box
            sx={{
              mt: 0.38,
              pl: 0.25,
              display: 'flex',
              gap: 0.55,
              overflowX: 'auto',
              overflowY: 'hidden',
              flexShrink: 0,
              alignItems: 'center',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { height: '3px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: ACCENT, borderRadius: '2px' },
              scrollbarWidth: 'thin',
              scrollbarColor: `${ACCENT} transparent`,
              opacity: agenda.enabled ? 1 : 0.55,
            }}
          >
            {summaryJobs.map((j) => {
              const colors = actionChipColors(String(j.actionType));
              const paused = !j.enabled || !agenda.enabled;
              return (
                <Box
                  key={j.id}
                  sx={{
                    flex: '0 0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.2,
                    px: 0.75,
                    py: 0.3,
                    borderRadius: '8px',
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    opacity: paused ? 0.42 : 1,
                    borderStyle: paused ? 'dashed' : 'solid',
                    maxWidth: 112,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                    <Typography
                      component="span"
                      sx={{
                        color: '#fff',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1,
                      }}
                    >
                      {formatJobTime(j)}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: colors.text,
                        fontSize: '0.58rem',
                        fontWeight: 600,
                        lineHeight: 1,
                        letterSpacing: 0.2,
                      }}
                    >
                      {actionShort(t, String(j.actionType))}
                    </Typography>
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      color: 'rgba(255,255,255,0.62)',
                      fontSize: '0.52rem',
                      fontWeight: 500,
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {chipDaysLine(t, j)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>

      <Dialog open={listOpen} onClose={() => setListOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: '#1a1a1a', color: '#fff' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <span>{t('deviceModal.agendaJobsTitle')}</span>
          <FormControlLabel
            control={<Switch checked={agenda.enabled} onChange={(e) => setAgenda({ ...agenda, enabled: e.target.checked })} size="small" />}
            label={t('deviceModal.agendaMaster') as string}
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: '#ccc' } }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>
            {t('deviceModal.agendaJobsCount', { count: agenda.jobs.length, max: MAX_JOBS })}
          </Typography>
          {agenda.jobs.map((j) => (
            <Box key={j.id} sx={{
              display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
              borderBottom: '1px solid #333', opacity: j.enabled ? 1 : 0.55,
            }}>
              <Switch
                size="small"
                checked={j.enabled}
                disabled={j.editable === false}
                onChange={(e) => {
                  setAgenda({
                    ...agenda,
                    jobs: agenda.jobs.map((x) => x.id === j.id ? { ...x, enabled: e.target.checked } : x),
                  });
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  {formatJobTime(j, caps.supportsSeconds)} · {actionLabel(t, String(j.actionType))}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
                  {daysShort(t, j.selectedDays)}
                  {jobRecurrenceExtra(t, j)}
                  {j.toggleEnabled && j.toggleAfterSeconds ? ` · ↔ ${formatToggleAfter(j.toggleAfterSeconds)}` : ''}
                  {j.brightness ? ` · ${j.brightness}%` : ''}
                  {j.editable === false ? ` · ${t('deviceModal.agendaNotEditable')}` : ''}
                </Typography>
              </Box>
              <IconButton size="small" disabled={j.editable === false} onClick={() => openEditor({ ...j })} sx={{ color: '#ccc' }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setAgenda({ ...agenda, jobs: agenda.jobs.filter((x) => x.id !== j.id) })} sx={{ color: '#f66' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {error && <Typography sx={{ color: '#f66', fontSize: '0.8rem', mt: 1 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={<AddIcon />}
            disabled={agenda.jobs.length >= MAX_JOBS}
            onClick={() => {
              const id = (agenda.jobs.reduce((m, j) => Math.max(m, j.id), 0) || 0) + 1;
              const nj = blankJob(id, device.tipo, device.generacion);
              setAgenda({ ...agenda, jobs: [...agenda.jobs, nj] });
              openEditor(nj);
            }}
            sx={{ color: ACCENT }}
          >
            {t('deviceModal.agendaAddJob')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setListOpen(false)} sx={{ color: '#aaa' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => saveFromList(agenda)}
            sx={{ backgroundColor: ACCENT }}
          >
            {t('deviceModal.agendaSaveDevice')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editJob)} onClose={closeEditor} fullWidth maxWidth="xs"
        PaperProps={{ sx: { backgroundColor: '#1a1a1a', color: '#fff' } }}>
        {editJob && (
          <>
            <Box sx={{ display: 'flex', borderBottom: '1px solid #444', px: 1 }}>
              {stepLabels.map((label, index) => (
                <Box
                  key={label}
                  onClick={() => setEditStep(index + 1)}
                  sx={{
                    flex: 1,
                    py: 1.25,
                    textAlign: 'center',
                    borderBottom: editStep === index + 1 ? `2px solid ${ACCENT}` : '2px solid transparent',
                    color: editStep === index + 1 ? ACCENT : '#aaa',
                    cursor: 'pointer',
                    '&:hover': { color: ACCENT, backgroundColor: 'rgba(35, 145, 255, 0.08)' },
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem' }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <DialogContent sx={{ pt: 2, minHeight: 280 }}>
              {editStep === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <DeferredNumberField
                      label={t('deviceModal.agendaHoursShort') as string}
                      size="small"
                      value={editJob.hours}
                      min={0}
                      max={23}
                      onCommit={(hours) => setEditJob({ ...editJob, hours })}
                      InputLabelProps={{ shrink: true }}
                      sx={TIME_FIELD_SX}
                    />
                    <DeferredNumberField
                      label={t('deviceModal.agendaMinutesShort') as string}
                      size="small"
                      value={editJob.minutes}
                      min={0}
                      max={59}
                      onCommit={(minutes) => setEditJob({ ...editJob, minutes })}
                      InputLabelProps={{ shrink: true }}
                      sx={TIME_FIELD_SX}
                    />
                    <DeferredNumberField
                      label={t('deviceModal.agendaSecondsShort') as string}
                      size="small"
                      value={caps.supportsSeconds ? editJob.seconds : 0}
                      min={0}
                      max={59}
                      disabled={!caps.supportsSeconds}
                      onCommit={(seconds) => setEditJob({ ...editJob, seconds })}
                      InputLabelProps={{ shrink: true }}
                      sx={TIME_FIELD_SX}
                    />
                  </Box>
                  {!caps.supportsSeconds && disabledHint}
                </Box>
              )}

              {editStep === 2 && (
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: '#888', mb: 0.5 }}>{t('deviceModal.days')}</Typography>
                  <FormGroup row sx={{ mb: 1.5 }}>
                    {DAY_KEYS.map((d) => (
                      <FormControlLabel
                        key={d}
                        control={
                          <Checkbox
                            size="small"
                            checked={editJob.selectedDays.includes(d)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...editJob.selectedDays, d]
                                : editJob.selectedDays.filter((x) => x !== d);
                              setEditJob({ ...editJob, selectedDays: next.length ? next : [d] });
                            }}
                          />
                        }
                        label={['', 'L', 'M', 'X', 'J', 'V', 'S', 'D'][d]}
                        sx={{ color: '#ccc', mr: 0.5 }}
                      />
                    ))}
                  </FormGroup>

                  <Box sx={{ mb: 1.5, opacity: caps.supportsDomMonths ? 1 : 0.55 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Switch
                        size="small"
                        disabled={!caps.supportsDomMonths}
                        checked={Boolean(editJob.specificDaysEnabled) && caps.supportsDomMonths}
                        onChange={(e) => setEditJob({
                          ...editJob,
                          specificDaysEnabled: e.target.checked,
                          selectedDaysOfMonth: e.target.checked ? (editJob.selectedDaysOfMonth || []) : [],
                        })}
                        sx={switchAccentSx}
                      />
                      <Typography sx={{ color: 'white', ml: 1, fontSize: '0.8rem' }}>
                        {t('deviceModal.selectSpecificDaysOfMonth')}
                      </Typography>
                    </Box>
                    {!caps.supportsDomMonths && disabledHint}
                    {caps.supportsDomMonths && editJob.specificDaysEnabled && (
                      <Box sx={{ mt: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: ACCENT, borderColor: ACCENT, fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                            onClick={() => {
                              const evenDays = Array.from({ length: 31 }, (_, i) => i + 1).filter((d) => d % 2 === 0);
                              setEditJob({
                                ...editJob,
                                selectedDaysOfMonth: Array.from(new Set([...(editJob.selectedDaysOfMonth || []), ...evenDays])),
                              });
                            }}
                          >
                            {t('deviceModal.even')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: ACCENT, borderColor: ACCENT, fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                            onClick={() => {
                              const oddDays = Array.from({ length: 31 }, (_, i) => i + 1).filter((d) => d % 2 === 1);
                              setEditJob({
                                ...editJob,
                                selectedDaysOfMonth: Array.from(new Set([...(editJob.selectedDaysOfMonth || []), ...oddDays])),
                              });
                            }}
                          >
                            {t('deviceModal.odd')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: ACCENT, borderColor: ACCENT, fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                            onClick={() => setEditJob({ ...editJob, selectedDaysOfMonth: [] })}
                          >
                            {t('deviceModal.clear')}
                          </Button>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                            const on = (editJob.selectedDaysOfMonth || []).includes(day);
                            return (
                              <Button
                                key={day}
                                size="small"
                                variant={on ? 'contained' : 'outlined'}
                                sx={chipBtnSx(on)}
                                onClick={() => {
                                  const cur = editJob.selectedDaysOfMonth || [];
                                  setEditJob({
                                    ...editJob,
                                    selectedDaysOfMonth: on ? cur.filter((d) => d !== day) : [...cur, day],
                                  });
                                }}
                              >
                                {day}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ opacity: caps.supportsDomMonths ? 1 : 0.55 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Switch
                        size="small"
                        disabled={!caps.supportsDomMonths}
                        checked={Boolean(editJob.specificMonthsEnabled) && caps.supportsDomMonths}
                        onChange={(e) => setEditJob({
                          ...editJob,
                          specificMonthsEnabled: e.target.checked,
                          selectedMonths: e.target.checked ? (editJob.selectedMonths || []) : [],
                        })}
                        sx={switchAccentSx}
                      />
                      <Typography sx={{ color: 'white', ml: 1, fontSize: '0.8rem' }}>
                        {t('deviceModal.selectSpecificMonths')}
                      </Typography>
                    </Box>
                    {!caps.supportsDomMonths && disabledHint}
                    {caps.supportsDomMonths && editJob.specificMonthsEnabled && (
                      <Box sx={{ mt: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: ACCENT, borderColor: ACCENT, fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                            onClick={() => {
                              const evenMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => m % 2 === 0);
                              setEditJob({
                                ...editJob,
                                selectedMonths: Array.from(new Set([...(editJob.selectedMonths || []), ...evenMonths])),
                              });
                            }}
                          >
                            {t('deviceModal.even')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: ACCENT, borderColor: ACCENT, fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                            onClick={() => {
                              const oddMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => m % 2 === 1);
                              setEditJob({
                                ...editJob,
                                selectedMonths: Array.from(new Set([...(editJob.selectedMonths || []), ...oddMonths])),
                              });
                            }}
                          >
                            {t('deviceModal.odd')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: ACCENT, borderColor: ACCENT, fontSize: '0.6rem', minWidth: 'auto', px: 0.5, py: 0.2, height: '20px' }}
                            onClick={() => setEditJob({ ...editJob, selectedMonths: [] })}
                          >
                            {t('deviceModal.clear')}
                          </Button>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                          {MONTH_KEYS.map((key, index) => {
                            const month = index + 1;
                            const on = (editJob.selectedMonths || []).includes(month);
                            return (
                              <Button
                                key={key}
                                size="small"
                                variant={on ? 'contained' : 'outlined'}
                                sx={{ ...chipBtnSx(on), minWidth: '40px', height: '25px' }}
                                onClick={() => {
                                  const cur = editJob.selectedMonths || [];
                                  setEditJob({
                                    ...editJob,
                                    selectedMonths: on ? cur.filter((m) => m !== month) : [...cur, month],
                                  });
                                }}
                              >
                                {t(key)}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {editStep === 3 && (
                <Box>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel sx={{ color: MUTED, fontSize: 12 }}>{t('deviceModal.agendaActionLabel')}</InputLabel>
                    <Select
                      value={editJob.actionType}
                      label={t('deviceModal.agendaActionLabel') as string}
                      onChange={(e) => setEditJob({ ...editJob, actionType: e.target.value })}
                      sx={{
                        color: '#fff',
                        fontSize: 12,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                        '& .MuiSvgIcon-root': { color: '#999' },
                      }}
                      MenuProps={{ PaperProps: { sx: MENU_PAPER_SX } }}
                    >
                      {caps.actions.map((a) => (
                        <MenuItem key={a} value={a}>{actionLabel(t, a)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {String(device.tipo || '').toUpperCase() === 'DIMMER2' && editJob.actionType === 'On' && (
                    <DeferredNumberField
                      fullWidth
                      size="small"
                      label={t('deviceModal.agendaBrightness') as string}
                      value={editJob.brightness ?? 50}
                      min={1}
                      max={100}
                      onCommit={(brightness) => setEditJob({ ...editJob, brightness })}
                      sx={{ mb: 2, input: { color: '#fff' }, label: { color: '#888' } }}
                    />
                  )}
                  {editJob.actionType === 'GoToPosition' && (
                    <DeferredNumberField
                      fullWidth
                      size="small"
                      label={t('deviceModal.agendaCoverPosition') as string}
                      value={editJob.coverPosition ?? 50}
                      min={0}
                      max={100}
                      onCommit={(coverPosition) => setEditJob({ ...editJob, coverPosition })}
                      sx={{ mb: 2, input: { color: '#fff' }, label: { color: '#888' } }}
                    />
                  )}

                  <Box sx={{ opacity: toggleAfterUsable ? 1 : 0.55, mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={!toggleAfterUsable}
                          checked={Boolean(editJob.toggleEnabled) && toggleAfterUsable}
                          onChange={(e) => setEditJob({ ...editJob, toggleEnabled: e.target.checked })}
                          sx={switchAccentSx}
                        />
                      }
                      label={t('deviceModal.agendaToggleAfter') as string}
                      sx={{ color: '#ccc', display: 'block', mb: 0.25 }}
                    />
                    {!toggleAfterUsable && disabledHint}
                    {toggleAfterUsable && editJob.toggleEnabled && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <DeferredNumberField
                          label={t('deviceModal.hours') as string}
                          size="small"
                          value={Math.floor((editJob.toggleAfterSeconds || 0) / 3600)}
                          min={0}
                          max={23}
                          emptyWhenZero
                          onCommit={(h) => {
                            const total = editJob.toggleAfterSeconds || 0;
                            const m = Math.floor((total % 3600) / 60);
                            const s = total % 60;
                            setEditJob({ ...editJob, toggleAfterSeconds: h * 3600 + m * 60 + s });
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={TIME_FIELD_SX}
                        />
                        <DeferredNumberField
                          label={t('deviceModal.minutes') as string}
                          size="small"
                          value={Math.floor(((editJob.toggleAfterSeconds || 0) % 3600) / 60)}
                          min={0}
                          max={59}
                          emptyWhenZero
                          onCommit={(m) => {
                            const total = editJob.toggleAfterSeconds || 0;
                            const h = Math.floor(total / 3600);
                            const s = total % 60;
                            setEditJob({ ...editJob, toggleAfterSeconds: h * 3600 + m * 60 + s });
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={TIME_FIELD_SX}
                        />
                        <DeferredNumberField
                          label={t('deviceModal.seconds') as string}
                          size="small"
                          value={(editJob.toggleAfterSeconds || 0) % 60}
                          min={0}
                          max={59}
                          emptyWhenZero
                          onCommit={(s) => {
                            const total = editJob.toggleAfterSeconds || 0;
                            const h = Math.floor(total / 3600);
                            const m = Math.floor((total % 3600) / 60);
                            setEditJob({ ...editJob, toggleAfterSeconds: h * 3600 + m * 60 + s });
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={TIME_FIELD_SX}
                        />
                      </Box>
                    )}
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editJob.enabled}
                        onChange={(e) => setEditJob({ ...editJob, enabled: e.target.checked })}
                        sx={switchAccentSx}
                      />
                    }
                    label={t('deviceModal.agendaJobActive') as string}
                    sx={{ color: '#ccc' }}
                  />
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={closeEditor} sx={{ color: '#aaa' }}>{t('common.cancel')}</Button>
              <Box sx={{ flex: 1 }} />
              {editStep > 1 && (
                <Button onClick={() => setEditStep((s) => Math.max(1, s - 1))} sx={{ color: '#ccc' }}>
                  {t('common.back') as string}
                </Button>
              )}
              {editStep < EDIT_STEPS ? (
                <Button variant="contained" onClick={() => setEditStep((s) => Math.min(EDIT_STEPS, s + 1))} sx={{ backgroundColor: ACCENT }}>
                  {t('common.next') as string}
                </Button>
              ) : (
                <Button variant="contained" onClick={commitEditor} sx={{ backgroundColor: ACCENT }}>
                  {t('common.done') as string}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); setPendingSave(null); }}
        PaperProps={{ sx: { backgroundColor: '#1a1a1a', color: '#fff' } }}>
        <DialogTitle>{t('deviceModal.agendaOverwriteTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#ccc', fontSize: '0.85rem', mb: 1 }}>
            {t('deviceModal.agendaOverwriteBody')}
          </Typography>
          {(pendingSave?.jobs || []).map((j) => (
            <Typography key={j.id} sx={{ fontSize: '0.8rem', color: '#eee' }}>
              {formatJobTime(j)} · {daysShort(t, j.selectedDays)}{jobRecurrenceExtra(t, j)} · {actionLabel(t, String(j.actionType))}
              {j.toggleEnabled && j.toggleAfterSeconds ? ` · ↔ ${formatToggleAfter(j.toggleAfterSeconds)}` : ''}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setPendingSave(null); }} sx={{ color: '#aaa' }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={async () => {
              if (!pendingSave) return;
              const ok = await persist(pendingSave, { confirmClear: true });
              setConfirmOpen(false);
              setPendingSave(null);
              if (ok) setListOpen(false);
            }}
            sx={{ backgroundColor: '#FFA000', color: '#111' }}
          >
            {t('deviceModal.agendaOverwriteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceAgendaPanel;
